import { SearchApiParams } from "../types/search";

/**
 * Returns clean/reset search parameters based on defaults from venueSearchParamsAtom
 * Preserves user location (userlatitude/userlongitude) and updates timestamp
 * 
 * @param currentParams - Current search params to extract user location from
 * @param isMapper - Whether user is a mapper (affects tagging_mode default)
 * @returns Clean search params with defaults
 */
export function getCleanSearchParams(
  currentParams: SearchApiParams,
  isMapper: boolean
): SearchApiParams {
  return {
    search: "",
    latitude: null,
    longitude: null,
    userlatitude: currentParams.userlatitude, // Preserve user's actual location
    userlongitude: currentParams.userlongitude, // Preserve user's actual location
    types: "",
    accessibilities: "",
    showVenueOptions: "All",
    addinfo: "",
    placesToReplaceString: "",
    searchThisAreaClicked: "no",
    zoom: "14", // Match home page map default zoom
    new_map: "1",
    tagging_mode: isMapper ? "on" : "off",
    progressive_update: "yes",
    userToken: currentParams.userToken, // Preserve user token
    saved_place_id: "",
    saved_place_user_id: "",
    saved_place_sortby: "",
    venue_id: undefined,
    search_type: undefined,
    search_value: undefined,
    unixTimestamp: Math.floor(Date.now() / 1000), // Update timestamp
    includeGoogleFallback: "false",
    openingHoursMode: undefined,
    openingHoursDay: undefined,
    openingHoursTime: undefined,
    mapping_status: undefined,
    section_filter: undefined,
    mapper_category: undefined,
    autoZoom: undefined,
    address: undefined,
  };
}

/**
 * Centralized cleanup function that resets all application state
 * to default values. Call this when user wants a "fresh start".
 * This is the single source of truth for all reset/cleanup operations.
 * 
 * @param options - Object containing state values and setter functions
 * @example
 * resetAllAppState({
 *   searchPageParams,
 *   setSearchPageParams,
 *   setViewMode,
 *   isMapper,
 *   setInternalSearchTerm,
 * });
 */
export function resetAllAppState(options: {
  /** Current search parameters */
  searchPageParams: SearchApiParams;
  /** Setter for search parameters */
  setSearchPageParams: (params: SearchApiParams | ((prev: SearchApiParams) => SearchApiParams)) => void;
  /** Setter for view mode (mapper/user toggle) */
  setViewMode: (mode: "mapper" | "user") => void;
  /** Whether the current user is a mapper */
  isMapper: boolean;
  /** Setter for internal search term (used in header/overlay layouts) */
  setInternalSearchTerm: (term: string) => void;
  // Future state resets can be added here as optional parameters:
  // setSelectedVenue?: (venue: Venue | null) => void;
  // setMapCenter?: (center: {lat: number, lng: number}) => void;
  // setFiltersOpen?: (open: boolean) => void;
}) {
  const {
    searchPageParams,
    setSearchPageParams,
    setViewMode,
    isMapper,
    setInternalSearchTerm,
  } = options;

  // Reset search params to defaults
  setSearchPageParams(getCleanSearchParams(searchPageParams, isMapper));

  // Reset view mode to default ("mapper")
  setViewMode("mapper");

  // Reset search term to default
  setInternalSearchTerm("");

  // Future resets can be added here:
  // Example: Reset map center to default region
  // if (options.setMapCenter) {
  //   options.setMapCenter({ lat: defaultRegion.latitude, lng: defaultRegion.longitude });
  // }
  
  // Example: Close filters sheet
  // if (options.setFiltersOpen) {
  //   options.setFiltersOpen(false);
  // }
}
